import { DashboardItem, useDashboardContext } from 'framework/ui/dashboard';
import { ControlledShow, useShowContext } from 'framework/show';
import { useListContext } from 'framework/list';
import { DashboardEmployee } from '../../types';
import EmployeeDashboardWrapper from '../../components/EmployeeDashboardExpandedItemWrapper';
import EmployeeDashboardItemCollapsed from './EmployeeDashboardItemCollapsed';
import EmployeeSummaryBottomContent from './EmployeeSummaryBottomContent';
import { DashboardFieldsProvider } from 'domain/shared/core/dashboard';
import { useDashboardCallbacks } from '../../hooks/useDashboardCallbacks';
import EmployeeDashboardModalsProvider from '../../components/EmployeeDashboardModalsProvider';
import { useEmployeeUrl } from 'domain/shared/employee';
import LinkIconButtonsEmployeeDashboard from '../../shared/buttons/LinkIconButtonsEmployeeDashboard';
import ContractsTimeline from '../../summary/contracts/ContractsTimeline';
import { Grid } from '@mui/system';
import { TimelineRefreshProvider } from './TimelineRefreshContext';
import { useZoneChangeHighlight } from '../../hooks/useZoneChangeHighlight';
import PersonCard from '../../shared/PersonCard';

type EmployeeDashboardItemProps = {
  employee: any;
  update: (data: any) => void;
  currentZone: string;
};

const EmployeeDashboardItemContent = () => {
  const { data } = useShowContext<DashboardEmployee>();
  const { data: { items = [] } = {} } = useListContext() || {};
  const { hasZoneChanged } = useZoneChangeHighlight(items as DashboardEmployee[]);
  const { expandedItems } = useDashboardContext();

  const employeeData = data.getData();
  const { getProfileUrl } = useEmployeeUrl();
  const itemId = employeeData.employeeAlias;
  const isExpanded = expandedItems.has(itemId);
  const isZoneChanged = hasZoneChanged(itemId) && !isExpanded;

  const dashboardCallbacks = useDashboardCallbacks();

  const collapsedComponent = <EmployeeDashboardItemCollapsed />;

  const expandedComponent = (
    <EmployeeDashboardWrapper bottomContent={<EmployeeSummaryBottomContent />}>
      <>
        <Grid>
          <PersonCard
            name={data.employeeName}
            photoId={data.employeePhotoId}
            positionId={data.positionId}
            primaryTechnology={data.primaryTechnology}
            secondaryTechnology={data.secondaryTechnology}
            competencyLevel={data.competencyLevel}
            status={data.employeeStatus}
            dateOfBirth={data.employeeBirthDate}
            profileUrl={getProfileUrl(data.employeeAlias)}
            showBirthday
            showTeams
            teamsComponent={<LinkIconButtonsEmployeeDashboard />}
          />
        </Grid>

        <ContractsTimeline />
      </>
    </EmployeeDashboardWrapper>
  );

  return (
    <DashboardFieldsProvider {...dashboardCallbacks}>
      <DashboardItem
        itemId={itemId}
        collapsedComponent={collapsedComponent}
        expandedComponent={expandedComponent}
        sx={isZoneChanged ? { backgroundColor: 'background.error' } : undefined}
      />
    </DashboardFieldsProvider>
  );
};

const EmployeeDashboardItem = ({ employee, update, currentZone }: EmployeeDashboardItemProps) => {
  return (
    <ControlledShow
      data={{
        ...employee,
        currentZone,
      }}
      setData={update}
    >
      <TimelineRefreshProvider>
        <EmployeeDashboardModalsProvider>
          <EmployeeDashboardItemContent />
        </EmployeeDashboardModalsProvider>
      </TimelineRefreshProvider>
    </ControlledShow>
  );
};

export default EmployeeDashboardItem;
